import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { useAuth } from "../hooks/useAuth";

// Create Context
const CartContext = createContext();

// Hook for using cart context easily
export const useCart = () => useContext(CartContext);

/* The one cart a signed-out visitor may read or write. Named rather than
   inlined because the sign-in adoption below has to name it too, and the bug
   it fixes was precisely two places disagreeing about which key is whose. */
const GUEST_CART_KEY = "cart_guest";

export const CartProvider = ({ children }) => {
  const [showCartOverlay, setShowCartOverlay] = useState(false);
  const [checkoutData, setCheckoutData] = useState({});
  const [cartItems, setCartItems] = useState({});
  const [itemCount, setItemCount] = useState(0);
  const isInitialMount = useRef(true);
  const { user } = useAuth();

  // Get user-specific cart key
  const getCartKey = () => {
    return user ? `cart_${user.id || user._id}` : GUEST_CART_KEY;
  };

  // Convert a cart item to the minimal persisted shape { listing_id, quantity }.
  // Handles both the new shape (pass-through) and the legacy full-snapshot shape
  // (which cached expiring image URLs and stale prices inside `card_listing`).
  const toMinimalItem = (item) => {
    if (item == null) return null;
    if (typeof item.listing_id === "number") {
      return { listing_id: item.listing_id, quantity: item.quantity || 0 };
    }
    const legacyId =
      item.card_listing?.id ??
      (item.listing_id != null ? Number(item.listing_id) : null);
    if (legacyId == null || Number.isNaN(Number(legacyId))) return null;
    return { listing_id: Number(legacyId), quantity: item.quantity || 0 };
  };

  // Migrate old cart format (seller names as keys, full card_listing snapshots)
  // to new format (seller IDs as keys, minimal { listing_id, quantity } items)
  const migrateCartFormat = (cart) => {
    const migratedCart = {};
    let needsMigration = false;

    for (const [key, items] of Object.entries(cart)) {
      // Check if key is numeric (new format) or string name (old format)
      const isNumericId = /^\d+$/.test(key);

      if (isNumericId) {
        const normalized = (items || []).map(toMinimalItem).filter(Boolean);
        if (normalized.length > 0) {
          migratedCart[key] = normalized;
          if (JSON.stringify(items) !== JSON.stringify(normalized)) {
            needsMigration = true;
          }
        }
      } else {
        // Old format with seller name as key, extract seller_id from items
        // (derive sellerId from card_listing.seller BEFORE normalizing --
        // legacy items still carry the seller object at migration time)
        needsMigration = true;
        if (items && items.length > 0) {
          const sellerId =
            items[0]?.card_listing?.seller?.id?.toString() ||
            items[0]?.card_listing?.seller_id?.toString();

          if (sellerId) {
            console.log(`[CartContext] Migrating cart key from "${key}" to seller ID "${sellerId}"`);
            const normalized = items.map(toMinimalItem).filter(Boolean);
            if (normalized.length > 0) {
              migratedCart[sellerId] = normalized;
            }
          } else {
            console.warn(`[CartContext] Could not migrate cart key "${key}" - no seller ID found`);
          }
        }
      }
    }

    if (needsMigration) {
      console.log("[CartContext] Cart migration completed:", migratedCart);
    }

    return migratedCart;
  };

  /* Fold a guest cart into the signed-in one. Union by seller, and where both
     hold the SAME listing keep the larger quantity rather than summing: the
     guest cart is usually the same intent expressed twice (add, bounce to
     login, sign in), so summing would silently double an order. Stock clamping
     happens downstream in `useCartWithListings`. */
  const mergeCarts = (userCart, guestCart) => {
    const merged = { ...userCart };
    for (const [sellerId, guestItems] of Object.entries(guestCart || {})) {
      const existing = merged[sellerId] || [];
      const byListing = new Map(existing.map((item) => [item.listing_id, { ...item }]));
      for (const item of guestItems || []) {
        const already = byListing.get(item.listing_id);
        if (already) {
          already.quantity = Math.max(already.quantity || 0, item.quantity || 0);
        } else {
          byListing.set(item.listing_id, { ...item });
        }
      }
      merged[sellerId] = [...byListing.values()];
    }
    return merged;
  };

  // Load cart from localStorage on mount or when user changes
  useEffect(() => {
    /* THE KEY IS THE IDENTITY, and it used to be a guess.
       With no user this searched localStorage for any `cart_*` key and took
       the first one, which on a shared device handed a signed-out visitor the
       previous buyer's cart -- and, through the sync effect below, let them
       write to it. A guest reads and writes `cart_guest`, nothing else. */
    const cartKey = user ? getCartKey() : GUEST_CART_KEY;

    /* SIGNING IN MUST NOT EMPTY YOUR CART. `cart_guest` and `cart_<id>` are
       different keys and nothing carried the first into the second, so the
       journey the app itself pushes people down -- add to cart, press
       Checkout, get bounced to login by ProtectedRoute, sign in -- created
       `cart_<id>` empty, orphaned `cart_guest` beside it, and showed "Your
       cart is empty". Adopt it once, then remove it so the next visitor on
       this device does not inherit it. */
    if (user) {
      const guestRaw = localStorage.getItem(GUEST_CART_KEY);
      if (guestRaw) {
        const guestCart = migrateCartFormat(JSON.parse(guestRaw) || {});
        localStorage.removeItem(GUEST_CART_KEY);
        if (Object.keys(guestCart).length > 0) {
          const ownCart = migrateCartFormat(JSON.parse(localStorage.getItem(cartKey)) || {});
          const adopted = mergeCarts(ownCart, guestCart);
          localStorage.setItem(cartKey, JSON.stringify(adopted));
          setCartItems(adopted);
          return;
        }
      }
    }

    if (cartKey) {
      const storedCart = JSON.parse(localStorage.getItem(cartKey)) || {};
      console.log("[CartContext] Initial load from localStorage:", storedCart);
      
      // Migrate old format to new format if needed
      const migratedCart = migrateCartFormat(storedCart);
      
      // Save migrated cart back to localStorage
      if (Object.keys(storedCart).length !== Object.keys(migratedCart).length || 
          JSON.stringify(storedCart) !== JSON.stringify(migratedCart)) {
        console.log("[CartContext] Saving migrated cart to localStorage");
        localStorage.setItem(cartKey, JSON.stringify(migratedCart));
      }
      
      setCartItems(migratedCart);
    } else {
      // Only clear cart if no cart key found at all
      console.log("[CartContext] No cart key found, clearing cart");
      setCartItems({});
    }
    
  }, [user]);

  // Sync cartItems to localStorage and update item count
  useEffect(() => {
    // Skip sync on initial mount (already loaded from localStorage)
    if (isInitialMount.current) {
      console.log("[CartContext] Skipping sync (initial mount)");
      isInitialMount.current = false;
      return;
    }

    /* Same rule as the load effect: the key follows the identity and is never
       searched for. Writing into `cartKeys[0]` while signed out could put a
       guest's items into a previous buyer's `cart_<id>` on a shared device. */
    const cartKey = user ? getCartKey() : GUEST_CART_KEY;
    
    if (cartKey) {
      console.log("[CartContext] Syncing cart to localStorage:", cartItems);
      localStorage.setItem(cartKey, JSON.stringify(cartItems));
    }

    // Flatten nested store-based structure to count items
    const totalItems = Object.values(cartItems)
      .flat() // merge all store arrays into one
      .reduce((sum, product) => sum + (product.quantity || 0), 0);

    console.log("[CartContext] Total items:", totalItems);
    setItemCount(totalItems);
  }, [cartItems]);

  // Clear cart for current user
  const clearCart = () => {
    if (user) {
      const cartKey = getCartKey();
      localStorage.removeItem(cartKey);
    }
    // Also clear old orderItems that might have wrong format
    const orderItemsKey = user
      ? `orderItems_${user.id || user._id}`
      : "orderItems_guest";
    localStorage.removeItem(orderItemsKey);
    setCartItems({});
  };

  const removeSellerItems = (sellerId) => {
    console.log(`[CartContext] Removing items from seller ID: ${sellerId}`);
    console.log(`[CartContext] Seller ID type:`, typeof sellerId);
    
    // Get cart key - if no user yet, try to find it from localStorage
    let cartKey;
    if (user) {
      cartKey = getCartKey();
    } else {
      // Try to find the cart key by looking for cart_* keys in localStorage
      const allKeys = Object.keys(localStorage);
      const cartKeys = allKeys.filter(key => key.startsWith('cart_'));
      
      if (cartKeys.length > 0) {
        // Use the most recently modified cart key (in case of multiple)
        cartKey = cartKeys[0];
        console.log(`[CartContext] No user, using found cart key: ${cartKey}`);
      } else {
        console.log("[CartContext] No user and no cart found in localStorage");
        return;
      }
    }

    const storedCart = JSON.parse(localStorage.getItem(cartKey)) || {};
    
    // Log all current seller IDs in cart for debugging
    console.log(`[CartContext] Current sellers in cart:`, Object.keys(storedCart));
    console.log(`[CartContext] Full cart before removal:`, storedCart);
    
    // Ensure sellerId is a string (cart keys are strings)
    const sellerIdStr = String(sellerId);
    
    if (storedCart[sellerIdStr]) {
      console.log(`[CartContext] Found seller ${sellerIdStr} in cart, removing...`);
      delete storedCart[sellerIdStr];
      
      setCartItems(storedCart);
      localStorage.setItem(cartKey, JSON.stringify(storedCart));
      console.log(`[CartContext] Seller ${sellerIdStr} removed. Remaining cart:`, storedCart);
      console.log(`[CartContext] Remaining sellers:`, Object.keys(storedCart));
    } else {
      console.log(`[CartContext] Seller ${sellerIdStr} not found in cart`);
      console.log(`[CartContext] Available seller IDs:`, Object.keys(storedCart));
    }
  };

  // Reload cart from localStorage (useful after external updates)
  const reloadCart = () => {
    console.log("[CartContext] reloadCart called");
    
    // Try to get cart key even without user
    let cartKey;
    if (user) {
      cartKey = getCartKey();
    } else {
      // Try to find the cart key by looking for cart_* keys in localStorage
      const allKeys = Object.keys(localStorage);
      const cartKeys = allKeys.filter(key => key.startsWith('cart_'));
      
      if (cartKeys.length > 0) {
        cartKey = cartKeys[0];
        console.log(`[CartContext] reloadCart - no user, using found cart key: ${cartKey}`);
      } else {
        console.log("[CartContext] No user and no cart found, clearing cart items");
        setCartItems({});
        return;
      }
    }
    
    const storedCart = JSON.parse(localStorage.getItem(cartKey)) || {};
    console.log("[CartContext] Loading cart from localStorage:", storedCart);
    setCartItems(storedCart);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        setCartItems,
        itemCount,
        clearCart,
        removeSellerItems,
        reloadCart,
        setShowCartOverlay,
        showCartOverlay,
        checkoutData,
         setCheckoutData
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
